<script lang="ts">
    import { onMount, untrack } from 'svelte';
    import { api } from '../lib/api';
    import { envs } from '../lib/stores/environment.svelte';
    import { toast } from '../lib/stores/toast.svelte';
    import type { Secret, Consumer, SecretGroup, SecretHistoryItem, SecretFetchesResponse } from '../lib/types';
    import { formatDate, formatDateLocale, formatRelativeLocale, usageStatus } from '../lib/format';
    import { t, i18n } from '../lib/i18n.svelte';

    let {
        secret,
        initialScope,
        consumers,
        groups,
        initialKeyName,
        scopeHint,
        onClose,
        onSaved,
    }: {
        secret: Secret | null;
        initialScope: { appScopeId: string | null; groupId: string | null } | null;
        consumers: Consumer[];
        groups: SecretGroup[];
        /** Prefill for NEW secrets (t_3ce8079f recovery flow from the miss ledger). */
        initialKeyName?: string | null;
        /** Scope-placement guidance hint (ext 3) shown above the scope select. */
        scopeHint?: string | null;
        onClose: () => void;
        onSaved: () => void;
    } = $props();

    // Use untrack() to capture the initial prop values without creating
    // a reactive dependency. The editor is mounted fresh each time the user
    // opens it, so these don't need to react to prop changes.
    const isNew = untrack(() => !secret);

    let keyName = $state(untrack(() => secret?.keyName ?? initialKeyName ?? ''));
    let value = $state('');
    let oldValue = $state<string | null>(null);
    let appScopeId = $state<string | null>(
        untrack(() => secret?.appScopeId ?? initialScope?.appScopeId ?? null)
    );
    let groupId = $state<string | null>(
        untrack(() => secret?.groupId ?? initialScope?.groupId ?? null)
    );
    let expiresAt = $state<string>(
        untrack(() => (secret?.expiresAt ? secret.expiresAt.slice(0, 10) : ''))
    );

    let showDiff = $state(false);
    let saving = $state(false);

    let history = $state<SecretHistoryItem[]>([]);
    let historyValue = $state<{ id: string; value: string } | null>(null);
    // Usage tracking (t_3ce8079f ext 1): per-consumer attribution for the
    // "Fetched by" drill-down. null until loaded (fetch failures are silent —
    // observability must never break the editor).
    let fetchInfo = $state<SecretFetchesResponse | null>(null);

    let dialog = $state<HTMLDialogElement>();

    $effect(() => {
        if (dialog && !dialog.open) dialog.showModal();
    });

    onMount(async () => {
        if (secret) {
            try {
                const r = await api<{ value: string }>(`/api/secrets/${secret.id}/reveal`);
                oldValue = r.value;
                value = r.value;
                const h = await api<SecretHistoryItem[]>(`/api/secrets/${secret.id}/history`);
                history = h;
            } catch (err: any) {
                toast.error(err.message);
            }
            // Fire-and-forget usage info: never blocks or breaks the editor.
            try {
                fetchInfo = await api<SecretFetchesResponse>(`/api/secrets/${secret.id}/fetches`);
            } catch {
                fetchInfo = null;
            }
        }
    });

    function close() {
        dialog?.close();
        onClose();
    }

    const availableGroups = $derived(
        groups.filter((g) => (g.appScopeId ?? null) === appScopeId)
    );

    async function save() {
        if (!keyName.match(/^[A-Z0-9_]+$/)) {
            toast.error('Key name must be UPPER_SNAKE_CASE.');
            return;
        }

        if (!isNew && value !== oldValue && !showDiff) {
            showDiff = true;
            return;
        }

        saving = true;
        try {
            const body: any = {
                keyName,
                appScopeId,
                groupId,
                expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            };
            if (isNew || value !== oldValue) body.value = value;

            if (isNew) {
                body.environmentId = envs.currentId;
                await api('/api/secrets', { method: 'POST', body: JSON.stringify(body) });
                toast.success('Secret created');
            } else {
                await api(`/api/secrets/${secret!.id}`, { method: 'PATCH', body: JSON.stringify(body) });
                toast.success('Secret updated');
            }
            onSaved();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            saving = false;
        }
    }

    async function loadHistoryValue(id: string) {
        if (historyValue?.id === id) { historyValue = null; return; }
        try {
            const r = await api<{ id: string; value: string }>(`/api/secrets/${secret!.id}/history/${id}/reveal`);
            historyValue = { id, value: r.value };
        } catch (err: any) {
            toast.error(err.message);
        }
    }

    async function rollback(historyId: string) {
        if (!confirm('Roll back to this version? The current value will be archived.')) return;
        try {
            await api(`/api/secrets/${secret!.id}/rollback/${historyId}`, { method: 'POST' });
            toast.success('Rolled back successfully');
            onSaved();
        } catch (err: any) {
            toast.error(err.message);
        }
    }
</script>

<dialog bind:this={dialog} onclose={onClose}>
    <header>
        <h2>{isNew ? 'New Secret' : `Edit ${secret!.keyName}`}</h2>
        <button class="ghost" onclick={close}>✕</button>
    </header>

    <div class="body">
        <label>
            Key Name <span class="muted">(UPPER_SNAKE_CASE)</span>
            <input bind:value={keyName} placeholder="DB_PASSWORD" />
        </label>

        <div class="row2">
            {#if scopeHint}
                <div class="scope-hint" style="grid-column: 1 / -1;">{scopeHint}</div>
            {/if}
            <label>
                Scope
                <select bind:value={appScopeId} onchange={() => (groupId = null)}>
                    <option value={null}>🌐 Global</option>
                    {#each consumers as c}
                        <option value={c.id}>📱 {c.name}</option>
                    {/each}
                </select>
            </label>
            <label>
                Group
                <select bind:value={groupId}>
                    <option value={null}>(ungrouped)</option>
                    {#each availableGroups as g}
                        <option value={g.id}>📁 {g.name}</option>
                    {/each}
                </select>
            </label>
        </div>

        <label>
            Value
            <textarea bind:value={value} rows="4" class="mono"></textarea>
        </label>

        <label>
            Expires (optional)
            <input type="date" bind:value={expiresAt} />
        </label>

        {#if showDiff && !isNew && oldValue !== null}
            <div class="diff">
                <h3>Confirm change</h3>
                <div class="diff-grid">
                    <div>
                        <div class="diff-label">Old</div>
                        <pre class="mono old">{oldValue}</pre>
                    </div>
                    <div>
                        <div class="diff-label">New</div>
                        <pre class="mono new">{value}</pre>
                    </div>
                </div>
            </div>
        {/if}

        {#if !isNew && secret}
            {@const usage = usageStatus(secret)}
            <div class="usage">
                <div class="usage-row">
                    <span class="usage-label">{t('usage.created', { en: 'Created', nb: 'Opprettet' })}</span>
                    <span>{formatDateLocale(secret.createdAt, i18n.locale)}</span>
                </div>
                <div class="usage-row" class:usage-grey={usage.level === 'never' || usage.level === 'never-pretracking'} class:usage-amber={usage.level === 'stale'}>
                    <span class="usage-label">{t('usage.lastFetched', { en: 'Last fetched', nb: 'Sist hentet' })}</span>
                    {#if secret.lastFetchedAt}
                        <span title={formatDateLocale(secret.lastFetchedAt, i18n.locale)}>
                            {formatRelativeLocale(secret.lastFetchedAt, i18n.locale)}
                            · {t('usage.count', { en: '{n} fetches', nb: '{n} hentinger' }, { n: secret.fetchedCount })}
                        </span>
                    {:else if usage.level === 'never'}
                        <span class="dim" title={t('usage.neverTooltip', {
                            en: 'Not fetched since usage tracking began (this secret is new).',
                            nb: 'Ikke hentet siden brukssporing startet (denne hemmeligheten er ny).',
                        })}>
                            {t('usage.never', { en: 'Never fetched', nb: 'Aldri hentet' })}
                        </span>
                    {:else}
                        <span class="dim" title={t('usage.preTrackingTooltip', {
                            en: 'Not fetched since usage tracking began — likely created before tracking existed.',
                            nb: 'Ikke hentet siden brukssporing startet — sannsynligvis opprettet før sporingen fantes.',
                        })}>
                            {t('usage.preTracking', { en: 'Not fetched since tracking began', nb: 'Ikke hentet siden sporing startet' })}
                        </span>
                    {/if}
                </div>
                {#if fetchInfo && fetchInfo.items.length > 0}
                    <div class="usage-row">
                        <span class="usage-label">{t('usage.fetchedBy', { en: 'Fetched by', nb: 'Hentet av' })}</span>
                        <span class="fetched-by">
                            {#each fetchInfo.items as f (f.id)}
                                <span class="consumer-chip" title={t('usage.consumerTooltip', {
                                    en: `First: ${formatDateLocale(f.firstFetchedAt, 'en')} · Last: ${formatDateLocale(f.lastFetchedAt, 'en')}`,
                                    nb: `Først: ${formatDateLocale(f.firstFetchedAt, 'nb')} · Sist: ${formatDateLocale(f.lastFetchedAt, 'nb')}`,
                                })}>
                                    {f.consumerAppName} ×{f.fetchedCount}
                                </span>
                            {/each}
                        </span>
                    </div>
                    {#if !secret.appScopeId && fetchInfo.items.length === 1}
                        <div class="usage-hint">
                            {t('usage.appScopeHint', {
                                en: `Only fetched by ${fetchInfo.items[0].consumerAppName} — consider app-scoping.`,
                                nb: `Bare hentet av ${fetchInfo.items[0].consumerAppName} — vurder app-omfang.`,
                            })}
                        </div>
                    {/if}
                {:else if !isNew}
                    <div class="usage-row">
                        <span class="usage-label">{t('usage.fetchedBy', { en: 'Fetched by', nb: 'Hentet av' })}</span>
                        <span class="dim">{t('usage.noConsumers', { en: 'No consumer fetches recorded yet', nb: 'Ingen forbrukerhentinger registrert ennå' })}</span>
                    </div>
                {/if}
            </div>
        {/if}

        {#if !isNew && history.length > 0}
            <details>
                <summary>History ({history.length})</summary>
                <ul class="history">
                    {#each history as h}
                        <li>
                            <div class="hist-meta">
                                <span class="badge info">v{h.version}</span>
                                <span class="muted">{formatDate(h.replacedAt)}</span>
                                <span class="dim">by {h.replacedBy}</span>
                            </div>
                            <div class="hist-actions">
                                <button class="ghost" onclick={() => loadHistoryValue(h.id)}>
                                    {historyValue?.id === h.id ? 'Hide' : 'Show'}
                                </button>
                                <button class="ghost" onclick={() => rollback(h.id)}>Rollback</button>
                            </div>
                            {#if historyValue?.id === h.id}
                                <pre class="mono hist-value">{historyValue.value}</pre>
                            {/if}
                        </li>
                    {/each}
                </ul>
            </details>
        {/if}
    </div>

    <footer>
        <button onclick={close}>Cancel</button>
        {#if showDiff}
            <button onclick={() => (showDiff = false)}>Back to edit</button>
        {/if}
        <button class="primary" onclick={save} disabled={saving}>
            {saving ? 'Saving...' : showDiff ? 'Confirm & Save' : isNew ? 'Create' : 'Save'}
        </button>
    </footer>
</dialog>

<style>
    header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--border); }
    header h2 { margin: 0; font-size: 16px; }
    .body { padding: 16px 20px; display: flex; flex-direction: column; gap: 14px; max-height: 70vh; overflow-y: auto; }
    footer { padding: 12px 20px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; }
    label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
    .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .scope-hint {
        font-size: 12px;
        color: var(--text-2);
        background: var(--bg-2);
        border: 1px dashed var(--border);
        border-radius: var(--radius);
        padding: 6px 10px;
    }
    .usage {
        display: flex;
        flex-direction: column;
        gap: 6px;
        background: var(--bg-0);
        border: 1px solid var(--border);
        border-radius: var(--radius);
        padding: 10px 12px;
        font-size: 12px;
    }
    .usage-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
    .usage-label {
        min-width: 90px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        font-size: 10px;
        color: var(--text-3);
    }
    .usage-grey { color: var(--text-3); }
    .usage-amber { color: var(--warning, #f59e0b); }
    .fetched-by { display: flex; gap: 6px; flex-wrap: wrap; }
    .consumer-chip {
        background: var(--bg-2);
        border: 1px solid var(--border);
        border-radius: 999px;
        padding: 1px 8px;
        font-size: 11px;
    }
    .usage-hint {
        font-size: 11px;
        color: var(--text-3);
        font-style: italic;
    }
    .diff { background: var(--bg-0); padding: 12px; border-radius: var(--radius); border: 1px solid var(--warning); }
    .diff h3 { margin: 0 0 8px; font-size: 13px; }
    .diff-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .diff-label { font-size: 11px; text-transform: uppercase; color: var(--text-3); margin-bottom: 4px; }
    pre { white-space: pre-wrap; word-break: break-all; margin: 0; padding: 8px; border-radius: var(--radius); font-size: 12px; }
    .old { background: rgba(239,68,68,0.12); }
    .new { background: rgba(16,185,129,0.12); }
    details summary { cursor: pointer; padding: 8px 0; font-weight: 500; }
    .history { list-style: none; padding: 0; margin: 0; }
    .history li { padding: 8px 0; border-bottom: 1px solid var(--border); display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .hist-meta { display: flex; gap: 8px; align-items: center; flex: 1; }
    .hist-value { width: 100%; background: var(--bg-2); margin-top: 6px; }
</style>